<template>
    <validation-observer ref="ownerStatementPublishForm" as="span">
        <modal
            :id="modalId"
            :title="modalTitle"
            :ok-title="okTitle"
            :ok-disabled="loading || isFormInvalid || isProcessingEmails || isLoadingInitialData"
            :with-background="true"
            @ok.prevent="handleOk"
            @show="onShow"
            @hidden="onHidden"
            size="lg"
            scrollable
        >
            <div v-if="loading && !isProcessingEmails" class="mb-3">
                <b-card class="border-0 shadow-sm">
                    <div class="text-center py-4">
                        <div class="mb-3">
                            <font-awesome-icon
                                :icon="['fas', isPublish ? 'upload' : 'archive']"
                                style="font-size: 2.5rem; color: #0d47a1;"
                                class="bounce-icon"
                            />
                        </div>
                        <h6 class="mb-3">
                            <span v-if="isPublish"
                                >Publishing Owner Statement{{ statementPostfix }}<span class="dots">...</span></span
                            >
                            <span v-else
                                >Unpublishing Owner Statement{{ statementPostfix }}<span class="dots">...</span></span
                            >
                        </h6>
                        <b-progress
                            :value="100"
                            height="4px"
                            animated
                            striped
                            variant="primary"
                            class="mx-auto mb-3"
                            style="max-width: 200px;"
                        ></b-progress>
                        <p class="text-muted small mb-0">
                            <span v-if="isPublish">
                                Please wait while we publish the statement{{ statementPostfix }}
                            </span>
                            <span v-else
                                >Please wait while we remove the statement{{ statementPostfix }} from published
                                status.</span
                            >
                        </p>
                    </div>
                </b-card>
            </div>

            <div v-show="!loading">
                <b-card class="mb-3" no-body>
                    <b-card-body class="p-4">
                        <h6 class="mb-4">
                            Are you sure you want to {{ action }} {{ statementPrefix }} Owner Statement{{
                                statementPostfix
                            }}?
                        </h6>

                        <div v-if="isPublish">
                            <div v-if="isLoadingInitialData" class="text-center py-4">
                                <div class="mb-3">
                                    <font-awesome-icon
                                        :icon="['fas', 'envelope-open-text']"
                                        style="font-size: 2rem; color: #0d47a1;"
                                        class="bounce-icon"
                                    />
                                </div>
                                <p class="text-muted mb-0">Loading email settings<span class="dots">...</span></p>
                            </div>
                            <b-row v-else>
                                <b-col cols="12" lg="6" class="mb-2">
                                    <div class="d-flex align-items-center">
                                        <form-switch
                                            v-model="sendEmailToOwner"
                                            @change="onSendEmailChange"
                                            :disabled="isLoadingInitialData"
                                        />
                                        <label class="mb-0 ml-2">Send Email to Owner</label>
                                    </div>
                                </b-col>
                                <b-col cols="12" lg="6" class="mb-2">
                                    <div class="d-flex align-items-center">
                                        <form-switch v-model="saveToOwnerAccount" :disabled="isLoadingInitialData" />
                                        <label class="mb-0 ml-2">Save to Owner Account</label>
                                    </div>
                                </b-col>
                            </b-row>
                        </div>
                        <h6 v-else class="mb-0">
                            This action will unpublish the selected owner statement{{ statementPostfix }}.
                        </h6>
                    </b-card-body>
                </b-card>

                <div v-if="isProcessingEmails" class="mb-3">
                    <b-card class="border-0 shadow-sm">
                        <div class="text-center py-4">
                            <div class="mb-3">
                                <font-awesome-icon
                                    :icon="['fas', 'envelope']"
                                    style="font-size: 2.5rem; color: #0d47a1;"
                                    class="bounce-icon"
                                />
                            </div>
                            <h6 class="mb-3">
                                <span v-if="totalCount === 1">Preparing Email<span class="dots">...</span></span>
                                <span v-else
                                    >Preparing Emails for {{ totalCount }} Owners<span class="dots">...</span></span
                                >
                            </h6>
                            <b-progress
                                :value="100"
                                height="4px"
                                animated
                                striped
                                variant="primary"
                                class="mx-auto mb-3"
                                style="max-width: 200px;"
                            ></b-progress>
                            <p class="text-muted small mb-0">
                                This may take a moment. Please wait while we prepare personalized emails for each owner.
                            </p>
                        </div>
                    </b-card>
                </div>

                <div v-if="sendEmailToOwner && emailsProcessed && allowToRender && isPublish">
                    <form-card
                        v-if="!isSingleOwnerSelected && localOwnersData && localOwnersData.length > 1"
                        title="Review and Customize Emails"
                        subtitle="Select an owner to preview or edit their email before sending"
                        :collapsible="false"
                        class="mb-3"
                    >
                        <template #header-actions>
                            <span class="badge badge-primary">
                                {{ emailCountMessage }}
                            </span>
                        </template>

                        <div class="mb-3">
                            <p class="text-muted mb-3">
                                <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-1" />
                                The default email template has been applied to all selected owners. You can review and
                                edit each owner's email by selecting them from the dropdown below.
                            </p>

                            <form-select
                                :value="selectedOwnerIndex"
                                :options="ownerSelectOptions"
                                floatingLabel="Select Owner"
                                @input="onOwnerSelectionChange"
                            />
                        </div>
                    </form-card>

                    <template v-if="!isSingleOwnerSelected && localOwnersData && localOwnersData.length > 0">
                        <OwnerStatementEmailForm
                            v-if="localOwnersData[selectedOwnerIndex]"
                            :key="`owner-${selectedOwnerIndex}-${formKey}`"
                            ref="emailForm"
                            :initial-to="localOwnersData[selectedOwnerIndex].email"
                            :owner-id="localOwnersData[selectedOwnerIndex].id"
                            :owner-name="localOwnersData[selectedOwnerIndex].name"
                            :owner-statement-id="localOwnersData[selectedOwnerIndex].statementId"
                            :team-member-options="teamMemberOptions"
                            :owner-options="ownerOptions"
                            :email-response-options="emailResponseOptions"
                            :initial-subject="localOwnersData[selectedOwnerIndex].emailSubject || ''"
                            :initial-message="localOwnersData[selectedOwnerIndex].emailMessage || ''"
                            :initial-cc="localOwnersData[selectedOwnerIndex].cc || ''"
                            :initial-bcc="localOwnersData[selectedOwnerIndex].bcc || ''"
                            :initial-cc-mode="localOwnersData[selectedOwnerIndex].cc_mode || 'OTHER'"
                            :initial-cc-id="localOwnersData[selectedOwnerIndex].cc_id || null"
                            :initial-selected-email-response="
                                localOwnersData[selectedOwnerIndex].selectedEmailResponseId || null
                            "
                            :auto-apply-response="false"
                            :is-using-unlayer="userIsUnlayerTester"
                            @validation-changed="onChildValidationChanged"
                        />
                    </template>
                    <template v-else-if="isSingleOwnerSelected && localOwnersData.length > 0">
                        <OwnerStatementEmailForm
                            ref="emailForm"
                            :initial-to="localOwnersData[0].email"
                            :owner-id="localOwnersData[0].id"
                            :owner-name="localOwnersData[0].name"
                            :owner-statement-id="localOwnersData[0].statementId"
                            :team-member-options="teamMemberOptions"
                            :owner-options="ownerOptions"
                            :email-response-options="emailResponseOptions"
                            :initial-subject="localOwnersData[0].emailSubject || ''"
                            :initial-message="localOwnersData[0].emailMessage || ''"
                            :initial-cc="localOwnersData[0].cc || ''"
                            :initial-bcc="localOwnersData[0].bcc || ''"
                            :initial-cc-mode="localOwnersData[0].cc_mode || 'OTHER'"
                            :initial-cc-id="localOwnersData[0].cc_id || null"
                            :initial-selected-email-response="localOwnersData[0].selectedEmailResponseId || null"
                            :auto-apply-response="true"
                            :is-using-unlayer="userIsUnlayerTester"
                            @validation-changed="onChildValidationChanged"
                        />
                    </template>
                </div>
            </div>

            <template #modal-footer="{ cancel }">
                <div class="w-100 d-flex flex-column flex-sm-row justify-content-end">
                    <b-button
                        variant="secondary"
                        class="mb-2 mb-sm-0 mr-sm-2"
                        :disabled="loading || isProcessingEmails || isLoadingInitialData"
                        @click="cancel()"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="primary"
                        :disabled="loading || isFormInvalid || isProcessingEmails || isLoadingInitialData"
                        @click="validateAndSubmit"
                    >
                        {{ okTitle }}
                    </b-button>
                </div>
            </template>
        </modal>
    </validation-observer>
</template>

<script>
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormCard from '../generic/form-card/form-card.vue';
    import OwnerStatementEmailForm from './owner-statement-email-form.vue';
    import API from '../../api';
    import {
        APP_EMAIL_RESPONSE_PROPERTY_MANAGEMENT,
        RESERVATION_INVOICE_CURRENT_TEAM_MEMBERS_ENDPOINT,
        RESERVATION_INVOICE_CURRENT_OWNERS_ENDPOINT,
        OWNER_STATEMENT_APPLY_EMAIL_RESPONSE,
        UNLAYER_TEMPLATE_BY_ID_ENDPOINT,
    } from '../../endpoints';
    import { mapGetters } from 'vuex';
    import { formatResponseError } from '../../utils/api';

    export default {
        name: 'OwnerStatementPublishUnpublishModal',
        components: {
            Modal,
            BusyModal,
            FormSwitch,
            FormSelect,
            FormCard,
            OwnerStatementEmailForm,
        },
        props: {
            modalId: {
                type: String,
                required: true,
            },
            action: {
                type: String,
                validator: (value) => ['publish', 'unpublish'].includes(value),
                required: true,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            isSingleOwnerSelected: {
                type: Boolean,
                default: false,
            },
            ownersData: {
                type: Array,
                default: null,
            },
            statementPrefix: {
                type: String,
                default: 'this',
            },
            statementPostfix: {
                type: String,
                default: '',
            },
            isBulkAction: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                allowToRender: false,
                sendEmailToOwner: false,
                saveToOwnerAccount: false,
                teamMemberOptions: [],
                ownerOptions: [],
                emailResponseOptions: [],
                childFormsInvalid: true,
                selectedOwnerIndex: 0,
                isProcessingEmails: false,
                totalCount: 0,
                emailsProcessed: false,
                localOwnersData: [],
                formKey: 0,
                isLoadingInitialData: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userID', 'userIsUnlayerTester']),
            isPublish() {
                return this.action === 'publish';
            },
            isFormInvalid() {
                if (!this.sendEmailToOwner)
                    return this.$refs.ownerStatementPublishForm && this.$refs.ownerStatementPublishForm.invalid;
                return (
                    (this.$refs.ownerStatementPublishForm && this.$refs.ownerStatementPublishForm.invalid) ||
                    this.childFormsInvalid
                );
            },
            modalTitle() {
                const capAction = this.action.charAt(0).toUpperCase() + this.action.slice(1);
                const extras = [];
                if (this.sendEmailToOwner) extras.push('Email');
                if (this.saveToOwnerAccount) extras.push('Save');
                if (extras.length === 1) return `${capAction} and ${extras[0]} Owner Statement${this.statementPostfix}`;
                if (extras.length === 2)
                    return `${capAction}, ${extras[0]}, and ${extras[1]} Owner Statement${this.statementPostfix}`;
                return `${capAction} Owner Statement${this.statementPostfix}`;
            },
            okTitle() {
                const capAction = this.action.charAt(0).toUpperCase() + this.action.slice(1);
                const extras = [];
                if (this.sendEmailToOwner) extras.push('Email');
                if (this.saveToOwnerAccount) extras.push('Save');
                return extras.length === 0 ? capAction : `${capAction} and ${extras.join(' and ')}`;
            },
            defaultEmailResponseId() {
                const defaultResponse = this.emailResponseOptions.find((option) => option.isDefault);
                return defaultResponse ? defaultResponse.value : null;
            },
            emailCountMessage() {
                const count = this.localOwnersData ? this.localOwnersData.length : 0;
                return count === 1 ? '1 email will be sent' : `${count} emails will be sent`;
            },
            ownerSelectOptions() {
                if (!this.localOwnersData) return [];
                return this.localOwnersData
                    .map((owner, index) => ({
                        value: index,
                        text: owner.name || `Owner ${index + 1}`,
                    }))
                    .sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));
            },
        },
        methods: {
            async onShow() {
                this.localOwnersData = this.ownersData ? this.ownersData.map((owner) => ({ ...owner })) : [];
                this.formKey = 0;
                this.isLoadingInitialData = true;

                await this.fetchData();

                this.setInitialSelectedOwner();
                this.allowToRender = true;
                this.isLoadingInitialData = false;

                await this.checkFormValidity();
            },
            onHidden() {
                this.allowToRender = false;
                this.sendEmailToOwner = false;
                this.saveToOwnerAccount = false;
                this.selectedOwnerIndex = 0;
                this.isProcessingEmails = false;
                this.totalCount = 0;
                this.emailsProcessed = false;
                this.localOwnersData = [];
                this.formKey = 0;
                this.isLoadingInitialData = false;
            },
            setInitialSelectedOwner() {
                if (!this.localOwnersData || this.localOwnersData.length === 0) {
                    this.selectedOwnerIndex = 0;
                    return;
                }
                const sortedOptions = this.ownerSelectOptions;
                if (sortedOptions.length > 0) {
                    this.selectedOwnerIndex = sortedOptions[0].value;
                }
            },
            async fetchData() {
                try {
                    // Fetch Team Members
                    const teamMembersResponse = await API.get(
                        RESERVATION_INVOICE_CURRENT_TEAM_MEMBERS_ENDPOINT(this.userID)
                    );
                    this.teamMemberOptions = teamMembersResponse.data.map((item) => ({
                        value: item.id,
                        text: item.name,
                    }));

                    // Fetch Owners
                    try {
                        const ownersResponse = await API.get(RESERVATION_INVOICE_CURRENT_OWNERS_ENDPOINT());
                        this.ownerOptions = ownersResponse.data.map((item) => ({
                            value: item.id,
                            text: item.name,
                        }));
                    } catch (e) {
                        this.ownerOptions = [];
                    }

                    if (this.userIsUnlayerTester) {
                        const unlayerResponse = await API.get(
                            UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'owner-statement-responses/'
                        );
                        this.emailResponseOptions = [
                            { value: null, text: 'Please select an email response' },
                            ...unlayerResponse.data.map((item) => ({
                                value: item.id,
                                text: item.name,
                                isDefault: item.identifier === 'owner_statements_download',
                            })),
                        ];
                    } else {
                        const emailResponse = await API.get(APP_EMAIL_RESPONSE_PROPERTY_MANAGEMENT());
                        this.emailResponseOptions = [
                            { value: null, text: 'Please select an email response' },
                            ...emailResponse.data.map((item) => ({
                                value: item.id,
                                text: item.title,
                                isDefault: item.is_default_owner_statement_notification_email === true,
                            })),
                        ];
                    }
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data.' });
                }
            },
            async batchApplyDefaultEmailResponse() {
                if (!this.defaultEmailResponseId || !this.localOwnersData || this.localOwnersData.length === 0) return;

                if (this.emailsProcessed) return;

                try {
                    this.isProcessingEmails = true;
                    this.totalCount = this.localOwnersData.length;

                    await this.$nextTick();

                    const statementIds = this.localOwnersData.map((owner) => owner.statementId);

                    const response = await API.post(OWNER_STATEMENT_APPLY_EMAIL_RESPONSE(), {
                        statement_ids: statementIds,
                        email_response_id: this.defaultEmailResponseId,
                        is_unlayer: this.userIsUnlayerTester,
                        subject_text: '',
                    });

                    if (response.data && response.data.results) {
                        response.data.results.forEach((result) => {
                            const ownerIndex = this.localOwnersData.findIndex(
                                (owner) => owner.statementId === result.statement_id
                            );
                            if (ownerIndex !== -1) {
                                const updatedOwner = {
                                    ...this.localOwnersData[ownerIndex],
                                    id: result.owner_id,
                                    name: result.owner_name,
                                    email: result.owner_email,
                                    emailSubject: result.subject,
                                    emailMessage: result.message,
                                    selectedEmailResponseId: this.defaultEmailResponseId,
                                };
                                this.$set(this.localOwnersData, ownerIndex, updatedOwner);
                            }
                        });
                        this.setInitialSelectedOwner();
                    }

                    this.emailsProcessed = true;
                    this.isProcessingEmails = false;
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not apply default email responses.' });
                    this.isProcessingEmails = false;
                }
            },
            async onSendEmailChange(value) {
                if (value) {
                    if (this.localOwnersData && this.localOwnersData.length > 0 && this.defaultEmailResponseId) {
                        await this.batchApplyDefaultEmailResponse();
                    }
                    await this.$nextTick();
                    this.checkFormValidity();
                } else {
                    this.childFormsInvalid = false;
                }
            },
            saveCurrentFormData() {
                if (this.$refs.emailForm && this.localOwnersData[this.selectedOwnerIndex]) {
                    const emailData = this.$refs.emailForm.getEmailData();
                    const emailResponseId = this.$refs.emailForm.getSelectedEmailResponseOption();
                    const currentOwner = this.localOwnersData[this.selectedOwnerIndex];
                    this.$set(currentOwner, 'email', emailData.to);
                    this.$set(currentOwner, 'emailSubject', emailData.subject);
                    this.$set(currentOwner, 'emailMessage', emailData.email_body);
                    this.$set(currentOwner, 'cc', emailData.cc || '');
                    this.$set(currentOwner, 'bcc', emailData.bcc || '');
                    this.$set(currentOwner, 'cc_mode', emailData.cc_mode || 'OTHER');
                    this.$set(currentOwner, 'cc_id', emailData.cc_id || null);
                    this.$set(currentOwner, 'selectedEmailResponseId', emailResponseId);
                }
            },
            onOwnerSelectionChange(newIndex) {
                this.saveCurrentFormData();
                this.selectedOwnerIndex = newIndex;
                this.formKey++;
            },
            onChildValidationChanged(isInvalid) {
                this.checkFormValidity();
            },
            async checkFormValidity() {
                if (!this.sendEmailToOwner) {
                    this.childFormsInvalid = false;
                    return;
                }

                if (this.$refs.emailForm) {
                    try {
                        const isInvalid = await this.$refs.emailForm.isFormInvalid();
                        this.childFormsInvalid = isInvalid;
                    } catch (error) {
                        this.childFormsInvalid = true;
                    }
                } else {
                    this.childFormsInvalid = true;
                }
            },
            async validateAndSubmit() {
                this.saveCurrentFormData();
                await this.checkFormValidity();
                if (!this.isFormInvalid) this.handleOk();
            },
            handleOk() {
                let emailData = null;
                if (this.sendEmailToOwner) {
                    if (!this.isSingleOwnerSelected) {
                        this.saveCurrentFormData();
                        emailData = this.localOwnersData.map((owner) => ({
                            to: owner.email,
                            subject: owner.emailSubject || '',
                            email_body: owner.emailMessage || '',
                            cc: owner.cc || '',
                            bcc: owner.bcc || '',
                            cc_mode: owner.cc_mode || 'OTHER',
                            cc_id: owner.cc_id || null,
                            owner_id: owner.id,
                            statement_id: owner.statementId,
                        }));
                    } else if (this.$refs.emailForm) {
                        const formData = this.$refs.emailForm.getEmailData();
                        const singleEmailData = {
                            ...formData,
                            owner_id: this.localOwnersData[0].id,
                            statement_id: this.localOwnersData[0].statementId,
                        };
                        emailData = this.isBulkAction ? [singleEmailData] : singleEmailData;
                    }
                }
                this.$emit('ok', {
                    saveToOwnerAccount: this.saveToOwnerAccount,
                    sendEmailToOwner: this.sendEmailToOwner,
                    emailData,
                });
            },
        },
        watch: {
            selectedOwnerIndex() {
                this.$nextTick(() => {
                    this.checkFormValidity();
                });
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../scss/loading-animations.scss';
</style>
